import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import './MetricDisplay.css';

interface MetricDisplayProps {
  value: number | string;
  label: string;
  unit?: string;
  icon?: React.ReactNode;
  comparison?: string;
  trend?: 'up' | 'down' | 'neutral';
  color?: 'forest' | 'earth' | 'gold' | 'blue' | 'red';
  size?: 'small' | 'medium' | 'large';
  animate?: boolean;
  delay?: number;
}

/**
 * MetricDisplay - Animated metric card with icon and comparison
 * Features number animation, trend indicators, and botanical styling
 */
export const MetricDisplay: React.FC<MetricDisplayProps> = ({
  value,
  label,
  unit = '',
  icon,
  comparison,
  trend = 'neutral',
  color = 'forest',
  size = 'medium',
  animate = true,
  delay = 0,
}) => {
  const [displayValue, setDisplayValue] = useState<number | string>(
    typeof value === 'number' ? 0 : value
  );

  // Animate number counting up
  useEffect(() => {
    if (typeof value === 'number' && animate) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setDisplayValue(Math.round(increment * currentStep));
        } else {
          setDisplayValue(value);
          clearInterval(timer);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    } else {
      setDisplayValue(value);
    }
  }, [value, animate]);

  const formatValue = (val: number | string): string => {
    if (typeof val === 'number') {
      return val.toLocaleString('en-IN');
    }
    return val;
  };

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: delay,
        ease: "easeOut" as const,
      },
    },
  };

  const trendIcons = {
    up: '↗',
    down: '↘',
    neutral: '→',
  };

  return (
    <motion.div
      className={`metric-display metric-display--${size} metric-display--${color}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {icon && <div className="metric-display__icon">{icon}</div>}

      <div className="metric-display__content">
        <div className="metric-display__value">
          {formatValue(displayValue)}
          {unit && <span className="metric-display__unit">{unit}</span>}
        </div>

        <div className="metric-display__label">{label}</div>

        {comparison && (
          <div className={`metric-display__comparison metric-display__comparison--${trend}`}>
            <span className="metric-display__trend-icon">{trendIcons[trend]}</span>
            {comparison}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MetricDisplay;
